import { useEffect, useState } from "react";
import { PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, User } from "lucide-react";
import api from "@/lib/api";

/**
 * Timeline PBX (Linkus/Yeastar) para o LeadDetail.
 *
 * Mostra as chamadas do PBX associadas ao lead pelo número de
 * telefone. Cada linha indica direção (in/out), disposition
 * (atendida / não atendida / ocupado / falha / voicemail), duração,
 * data e — quando disponível — o utilizador Caring associado à
 * extensão que atendeu / marcou.
 *
 * Silencioso quando não há chamadas: devolve `null` para não poluir
 * a ficha do lead com secções vazias.
 */
export default function LinkusCallsTimeline({ leadId }) {
  const [state, setState] = useState({ loading: true, calls: [] });

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/linkus/lead/${leadId}/calls`);
        if (!cancelled) {
          setState({ loading: false, calls: data.items || [] });
        }
      } catch {
        if (!cancelled) setState({ loading: false, calls: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  if (state.loading || state.calls.length === 0) return null;

  return (
    <div
      className="bg-card border border-border rounded-2xl p-6 lg:p-8"
      data-testid="linkus-timeline"
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-ui text-lg flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-primary" />
          Chamadas PBX (Linkus)
        </h3>
        <span className="text-xs text-muted-foreground">
          {state.calls.length} chamada{state.calls.length === 1 ? "" : "s"}
        </span>
      </div>

      <ol className="space-y-3">
        {state.calls.map((c) => (
          <CallRow key={c.uid} call={c} />
        ))}
      </ol>
    </div>
  );
}

function CallRow({ call }) {
  const isOutbound = call.direction === "outbound";
  const answered = call.answered;
  const Icon = !answered && isOutbound
    ? PhoneMissed
    : isOutbound
    ? PhoneOutgoing
    : PhoneIncoming;

  const tone = !answered
    ? { bg: "bg-[hsl(var(--danger-bg))]", ring: "ring-rose-200", icon: "text-[hsl(var(--danger))]", label: "text-[hsl(var(--danger))]" }
    : isOutbound
    ? { bg: "bg-[hsl(var(--success-bg))]", ring: "ring-emerald-200", icon: "text-[hsl(var(--success))]", label: "text-[hsl(var(--success))]" }
    : { bg: "bg-[hsl(var(--info-bg))]", ring: "ring-sky-200", icon: "text-[hsl(var(--info))]", label: "text-[hsl(var(--info))]" };

  const directionLabel = isOutbound ? "Saída" : call.direction === "inbound" ? "Entrada" : "Interna";
  const dispositionLabel = {
    ANSWERED: "Atendida",
    "NO ANSWER": "Não atendida",
    BUSY: "Ocupado",
    FAILED: "Falha",
    VOICEMAIL: "Voicemail",
  }[call.disposition] || call.disposition;

  // Quem atendeu / originou: o campo do lado da extensão (não do cliente)
  const extensionName = isOutbound ? call.caller_name : call.called_name;
  const extensionUser = call.matched_user_name || extensionName || null;
  const extensionNumber = isOutbound ? call.caller_number : call.called_number;

  // Formatar duração
  const dur = call.talk_duration_seconds || call.duration_seconds || 0;
  const durText = dur >= 60
    ? `${Math.floor(dur / 60)}m ${dur % 60}s`
    : `${dur}s`;

  return (
    <li className="flex items-start gap-3" data-testid={`linkus-call-${call.uid}`}>
      <div
        className={`w-9 h-9 rounded-full flex items-center justify-center ${tone.bg} ring-1 ${tone.ring} shrink-0`}
      >
        <Icon className={`w-4 h-4 ${tone.icon}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${tone.label}`}>
            {directionLabel}
          </span>
          <span className="text-xs text-muted-foreground px-2 py-0.5 rounded bg-muted">
            {dispositionLabel}
          </span>
          {dur > 0 && (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {durText}
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono">{call.date || ""}</span>
          {extensionUser && (
            <span className="inline-flex items-center gap-1">
              <User className="w-3 h-3 text-muted-foreground" />
              <span>{extensionUser}</span>
              {extensionNumber && (
                <span className="text-muted-foreground font-mono">
                  · ext. {extensionNumber}
                </span>
              )}
            </span>
          )}
          {!extensionUser && extensionNumber && (
            <span className="text-muted-foreground">ext. {extensionNumber}</span>
          )}
        </div>
      </div>
    </li>
  );
}
